// IE Hub - Field Help Dialog Component
// Responsive help dialog for field-specific instructions
// Mobile: bottom slide-up, Desktop: right drawer

import React, { useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Drawer,
  IconButton,
  Link,
  Slide,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';

export interface HelpStep {
  stepNumber?: number;
  text: string;
}

export interface FieldHelpContent {
  title: string;
  instructionsTitle?: string;
  steps: HelpStep[];
  /** When false, the caller is responsible for numbering within step text. */
  showStepNumbers?: boolean;
  image?: string;
  imageAlt?: string;
  bottomLinkText?: string;
  onBottomLinkClick?: () => void;
}

interface FieldHelpDialogProps {
  open: boolean;
  onClose: () => void;
  content: FieldHelpContent;
  zIndex?: number;
}

// Slide transition for mobile (from bottom)
const SlideUpTransition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} appear={props.appear ?? true} />;
});

export default function FieldHelpDialog({ open, onClose, content, zIndex = 1400 }: FieldHelpDialogProps): JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Touch gesture handling for mobile swipe-to-dismiss
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragDistance, setDragDistance] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Minimum swipe distance (in px) to trigger close
  const minSwipeDistance = 50;

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    const touch = e.targetTouches[0];
    if (touch) {
      setTouchStart(touch.clientY);
      setIsDragging(true);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;
    
    const touch = e.targetTouches[0];
    if (!touch) return;
    
    const currentTouch = touch.clientY;
    const distance = currentTouch - touchStart;
    
    // Only allow downward swipes (positive distance)
    if (distance > 0) {
      setDragDistance(distance);
      setTouchEnd(currentTouch);
    }
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setIsDragging(false);
      setDragDistance(0);
      return;
    }

    const distance = touchEnd - touchStart;
    
    // Close dialog if swiped down enough
    if (distance > minSwipeDistance) {
      onClose();
    }
    
    // Reset states
    setTouchStart(null);
    setTouchEnd(null);
    setIsDragging(false);
    setDragDistance(0);
  };

  const handleBottomLinkClick = () => {
    if (content.onBottomLinkClick) {
      content.onBottomLinkClick();
    }
  };

  const instructionsHeading =
    content.instructionsTitle || 'Instructions to find ID';
  const shouldShowInstructionsHeading =
    instructionsHeading.trim().toLowerCase() !==
    (content.title?.trim().toLowerCase() || '');
  const showStepNumbers = content.showStepNumbers !== false;

  const helpContent = (
    <Box sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative'
    }}>
      {/* Header with Close Button */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: { xs: 2, md: 3 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        minHeight: '64px'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', fontSize: { xs: '1.125rem', md: '1.25rem' } }}>
          {content.title}
        </Typography>
        <IconButton 
          onClick={onClose}
          sx={{ 
            p: 1,
            backgroundColor: 'action.hover',
            '&:hover': {
              backgroundColor: 'action.selected'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ 
        flex: 1, 
        p: { xs: 2, md: 3 },
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Stack spacing={3} sx={{ flex: 1 }}>
          {/* Instructions Section */}
          <Box>
            {shouldShowInstructionsHeading && (
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  mb: 2, 
                  fontWeight: 600, 
                  color: 'primary.main',
                  fontSize: { xs: '1rem', md: '1.125rem' }
                }}
              >
                {instructionsHeading}
              </Typography>
            )}
            <Stack spacing={1.5}>
              {content.steps.map((step, index) => {
                const numberLabel =
                  showStepNumbers && (step.stepNumber ?? index + 1);

                return (
                  <Box 
                    key={index}
                    sx={{ 
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: numberLabel ? 1 : 0
                    }}
                  >
                    {numberLabel ? (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600,
                          color: 'text.primary',
                          minWidth: '24px',
                          lineHeight: 1.6
                        }}
                      >
                        {`${numberLabel}.`}
                      </Typography>
                    ) : null}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary', 
                        lineHeight: 1.6,
                        flex: 1
                      }}
                    >
                      {step.text}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>

          {/* Image Section */}
          {content.image && (
            <Box 
              sx={{ 
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2,
                backgroundColor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Box
                component="img"
                src={content.image}
                alt={content.imageAlt || 'Help illustration'}
                sx={{
                  maxWidth: '100%',
                  height: 'auto',
                  maxHeight: { xs: '200px', md: '300px' },
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}
        </Stack>

        {/* Bottom Link */}
        {content.bottomLinkText && (
          <Box 
            sx={{ 
              pt: 2,
              mt: 'auto',
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Link
              component="button"
              onClick={handleBottomLinkClick}
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
                textDecoration: 'underline',
                color: 'primary.main',
                cursor: 'pointer',
                border: 'none',
                background: 'none',
                padding: 0,
                textAlign: 'left',
                '&:hover': {
                  color: 'primary.dark',
                  textDecoration: 'underline'
                }
              }}
            >
              {content.bottomLinkText}
            </Link>
          </Box>
        )}
      </Box>
    </Box>
  );

  if (isMobile) {
    // Mobile: Full-screen dialog sliding from bottom with swipe-to-dismiss
    return (
      <Dialog
        open={open}
        onClose={onClose}
        TransitionComponent={SlideUpTransition}
        fullScreen
        keepMounted
        style={{ zIndex: zIndex }}
        BackdropProps={{
          style: { zIndex: zIndex - 1 }
        }}
        PaperProps={{
          sx: {
            zIndex: zIndex,
            backgroundColor: 'background.default',
            backgroundImage: 'none',
            margin: 0,
            borderRadius: '16px 16px 0 0', // Top corners rounded for mobile
            transform: isDragging ? `translateY(${Math.min(dragDistance, 100)}px)` : 'none',
            transition: isDragging ? 'none' : 'transform 0.3s ease-out',
            opacity: isDragging ? Math.max(1 - dragDistance / 200, 0.7) : 1
          }
        }}
      >
        <DialogContent 
          ref={dialogRef}
          sx={{ p: 0, height: '100%' }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Swipe Indicator */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            pt: 1,
            pb: 0.5
          }}>
            <Box sx={{
              width: 36,
              height: 4,
              backgroundColor: 'action.disabled',
              borderRadius: 2,
              opacity: 0.6
            }} />
          </Box>
          {helpContent}
        </DialogContent>
      </Dialog>
    );
  }

  // Desktop: Drawer sliding from right
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      keepMounted
      ModalProps={{
        style: { zIndex: zIndex }
      }}
      PaperProps={{
        sx: {
          width: { md: '400px', lg: '480px' },
          maxWidth: '40vw',
          backgroundColor: 'background.paper',
          border: 'none',
          boxShadow: theme.shadows[8],
          borderRadius: '16px 0 0 16px' // Left side rounded for desktop
        }
      }}
    >
      {helpContent}
    </Drawer>
  );
}

